import store from 'store/store';
import { Core } from '@pdftron/webviewer';
import {
  CropZoneCustomData,
  DocumentTypes,
  GraphicZoneCustomData,
  InputAnnotation,
  MarqueeZoneCustomData,
  PDFTronTools,
  PolygonZoneCustomData,
  ZoneCustomData,
} from 'types';
import { getAnnotations, inspection } from 'store';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import WebViewerWrapper from 'pdftron/core/WebViewerWrapper';

const zoneIdByToolName: Record<string, string> = {
  [PDFTronTools.ZONE]: ZoneCustomData.zoneId,
  [PDFTronTools.GRAPHIC]: GraphicZoneCustomData.zoneNumber,
  [PDFTronTools.MARQUEE_ZONE]: MarqueeZoneCustomData.zoneId,
};

export const zoneTypeByToolName: Record<string, string> = {
  [PDFTronTools.ZONE]: ZoneCustomData.zoneAnnotation,
  [PDFTronTools.GRAPHIC]: GraphicZoneCustomData.graphicAnnotation,
  [PDFTronTools.MARQUEE_ZONE]: MarqueeZoneCustomData.marqueeZoneAnotation,
  [PDFTronTools.CROP]: CropZoneCustomData.cropZoneAnnotation,
  [PDFTronTools.POLYGON]: PolygonZoneCustomData.polygonAnnotation,
};

const reduxActionByToolName: Record<string, any> = {
  [PDFTronTools.ZONE]: inspection.actions.setTextZoneId,
  [PDFTronTools.GRAPHIC]: inspection.actions.setGraphicZoneId,
  [PDFTronTools.MARQUEE_ZONE]: inspection.actions.setMarqueeZoneId,
  [PDFTronTools.CROP]: inspection.actions.setCropZoneId,
  [PDFTronTools.POLYGON]: inspection.actions.setCropZoneId,
};

/**
 * This function is used to reset the zones sequence. Typically used after deleting a zone or excluding pages.
 * @param refAnnotation (Optional) The annotation that was interacted with and caused the shaking - if not specified, it shakes for all types
 */
export function shakeDocumentsZones(refAnnotation?: Core.Annotations.Annotation) {
  const docManager = PDFManagerFactory.getPDFDocManager();
  // when a ref annotation is passed, it means we only care about shaking the same type of annotations as the reference. Otherwise we do it for all of possible zone types.
  let zoneTypesToShake = Object.keys(zoneTypeByToolName);
  if (refAnnotation) {
    const zoneType = refAnnotation.getCustomData(zoneTypeByToolName[refAnnotation.ToolName]) === 'true';
    if (zoneType) {
      zoneTypesToShake = [refAnnotation.ToolName];
    }
  }

  const reduxAnnotations: Record<string, InputAnnotation[]> = getAnnotations(store.getState());
  const sourceAnnotations = reduxAnnotations[DocumentTypes.source];
  const targetAnnotations = reduxAnnotations[DocumentTypes.target];

  zoneTypesToShake.forEach((zoneType) => {
    const zoneIdCustomData = zoneIdByToolName[zoneType];

    [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
      const annotations = docType === DocumentTypes.source ? sourceAnnotations : targetAnnotations;
      const docInstance = docManager?.[docType];

      const annotationsSortedByZoneNumber = annotations
        .filter((annotation) => annotation.usedTool === zoneType)
        .sort((a, b) => {
          const annotA = docInstance?.getAnnotationById(a.annotationId);
          const annotB = docInstance?.getAnnotationById(b.annotationId);
          return (
            Number(annotA?.getCustomData(zoneIdCustomData) || 0) - Number(annotB?.getCustomData(zoneIdCustomData) || 0)
          );
        });

      let currentZoneNumber = 0;
      annotationsSortedByZoneNumber.forEach((annotation) => {
        const annot = docInstance?.getAnnotationById(annotation.annotationId);
        if (!annot) return;
        const annotZoneNumber = parseInt(annot.getCustomData(zoneIdCustomData) || '0', 10);
        if (annotZoneNumber !== currentZoneNumber) {
          currentZoneNumber++;
          annot.setCustomData(zoneIdCustomData, `${currentZoneNumber}`);
        }
        docInstance?.annotationManager.redrawAnnotation(annot);
      });

      // change current zone id in redux so next annotation that we draw follows the sequence (and only do it for source)
      if (docType === DocumentTypes.source && reduxActionByToolName[zoneType]) {
        store.dispatch(reduxActionByToolName[zoneType](currentZoneNumber + 1));
      }
    });
  });
}

/**
 * Resets nonSelectable on both Text and Marquee zones across source and target documents.
 * Use after unlink flows to re-enable selection globally.
 */
export function resetAllNonSelectableFlags() {
  const docManager = PDFManagerFactory.getPDFDocManager();
  const source = docManager?.getInstance?.(DocumentTypes.source);
  const target = docManager?.getInstance?.(DocumentTypes.target);
  const resetIn = (instance: WebViewerWrapper) => {
    const list: Core.Annotations.Annotation[] = instance.annotationManager.getAnnotationsList();
    list.forEach((annot) => {
      const isText = annot.ToolName === PDFTronTools.ZONE;
      const isMarquee = annot.ToolName === PDFTronTools.MARQUEE_ZONE;
      if (isText && annot.getCustomData(ZoneCustomData.nonSelectable) === 'true') {
        annot.setCustomData(ZoneCustomData.nonSelectable, 'false');
      }
      if (isMarquee && annot.getCustomData(MarqueeZoneCustomData.nonSelectable) === 'true') {
        annot.setCustomData(MarqueeZoneCustomData.nonSelectable, 'false');
      }
    });
  };
  if (source) {
    resetIn(source);
  }
  if (target) {
    resetIn(target);
  }
}
